import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Edit, CreditCard, Loader2, ShoppingCart, Printer } from 'lucide-react';
import { useDiningAreas } from '@/features/pos/hooks/useDiningAreas';
import { useRestaurantTables } from '@/features/pos/hooks/useRestaurantTables';
import { useTableOrder, useUpdateOrderStatus, useDeleteOrder, useCreateOrder } from '@/features/pos/hooks/usePOSOrders';
import { usePOSCartStore } from '@/features/pos/stores/posCartStore';
import { toast } from '@/components/ui/toast/useToast';
import { PrintManager } from './receipts/PrintManager';
import { useState } from 'react';

interface TableOrderDialogProps {
  open: boolean;
  onClose: () => void;
  areaId: string;
  tableId: string;
  tableNumber: string;
  onSwitchToProducts?: () => void;
}

export const TableOrderDialog = ({
  open,
  onClose,
  areaId,
  tableId,
  tableNumber,
  onSwitchToProducts,
}: TableOrderDialogProps) => {
  const { data: areas = [] } = useDiningAreas();
  const { data: allTables = [] } = useRestaurantTables();
  const { data: order, isLoading } = useTableOrder(tableId);
  const updateOrderStatus = useUpdateOrderStatus();
  const deleteOrder = useDeleteOrder();
  const createOrder = useCreateOrder();
  const { linkToTable, clearCart, loadItems } = usePOSCartStore();
  const [showPrintManager, setShowPrintManager] = useState(false);
  
  const area = areas.find(a => a.id === areaId);
  const table = allTables.find(t => t.id === tableId);

  // Calculate duration
  const duration = order ? Math.floor((new Date().getTime() - new Date(order.started_at).getTime()) / 60000) : 0;

  const handlePay = async () => {
    if (!order) return;
    
    try {
      await updateOrderStatus.mutateAsync({
        order_id: order.id,
        status: 'completed',
      });
      
      // Clear cart after payment
      clearCart();
      
      toast.success('Paiement', 'Commande payée et table libérée');
      onClose();
    } catch (error: any) {
      toast.error('Erreur', error.message);
    }
  };

  const handleEdit = () => {
    if (!order) return;
    
    // Clear cart first
    clearCart();
    
    // Load order items into cart with correct quantities
    const cartItems = order.items?.map((item) => ({
      productId: item.product_id,
      productCode: item.product_code,
      productName: item.product_name,
      quantity: item.quantity, // ← Preserve quantity!
      unitPrice: item.unit_price,
      vat: item.vat_rate,
      discount: item.discount_rate,
      discountType: 'percentage' as const,
      notes: item.notes,
    })) || [];
    
    loadItems(cartItems);
    
    // Link cart to table (keep editing same order)
    linkToTable(tableId, tableNumber, areaId, order.id);
    
    // Switch to Products tab
    if (onSwitchToProducts) {
      onSwitchToProducts();
    }
    
    toast.info('Modification', `Modification de la commande ${tableNumber}`);
    onClose();
  };

  const handleCancel = async () => {
    if (!order) return;
    
    if (confirm('Annuler la commande pour cette table?')) {
      try {
        await deleteOrder.mutateAsync(order.id);
        
        // Clear cart after cancellation
        clearCart();
        
        toast.success('Annulée', 'Commande annulée et table libérée');
        onClose();
      } catch (error: any) {
        toast.error('Erreur', error.message);
      }
    }
  };

  const handleNewOrder = async () => {
    try {
      // Clear any existing cart first
      clearCart();
      
      // Create new order for this table
      const newOrder = await createOrder.mutateAsync({
        order_type: 'dine-in',
        table_id: tableId,
        area_id: areaId,
      });

      // Link cart to this table and order
      linkToTable(tableId, tableNumber, areaId, newOrder.id);

      // Close dialog
      onClose();

      // Switch to Products tab
      if (onSwitchToProducts) {
        onSwitchToProducts();
      }

      toast.success(
        'Nouvelle commande',
        `Commande créée pour ${tableNumber}. Ajoutez des produits.`
      );
    } catch (error: any) {
      toast.error('Erreur', error.message);
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!table) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-3">
            <span>{area?.name}</span>
            <span className="text-gray-400">/</span>
            <span className="text-emerald-600">{tableNumber}</span>
            {table.table_name && (
              <>
                <span className="text-gray-400">-</span>
                <span className="text-gray-600 text-lg">{table.table_name}</span>
              </>
            )}
          </DialogTitle>
          <p className="text-sm text-gray-500">
            {table.seats} places • {table.shape}
          </p>
        </DialogHeader>

        {!order ? (
          <div className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg text-gray-600 mb-4">Table disponible</p>
            <p className="text-sm text-gray-500 mb-6">
              Commencez une nouvelle commande pour cette table
            </p>
            <Button 
              size="lg" 
              className="gap-2" 
              onClick={handleNewOrder}
              disabled={createOrder.isPending}
            >
              {createOrder.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" />
                  Nouvelle commande
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Order Summary */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Commande en cours</h3>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">#{order.order_number}</span>
                  <span className="text-gray-500">Durée: {duration} min</span>
                </div>
              </div>

              {/* Order Items */}
              {order.items && order.items.length > 0 ? (
                <div className="bg-muted rounded-lg p-4 space-y-3">
                  {order.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-card rounded-full flex items-center justify-center text-sm font-medium">
                          {item.quantity}
                        </span>
                        <div>
                          <span className="text-gray-900">{item.product_name}</span>
                          {item.notes && (
                            <p className="text-xs text-gray-500 italic">Note: {item.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {item.total_amount.toFixed(2)} DA
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.unit_price.toFixed(2)} DA × {item.quantity}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-muted rounded-lg p-8 text-center text-muted-foreground">
                  <p>Aucun article dans la commande</p>
                </div>
              )}

              {/* Totals */}
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total</span>
                  <span className="text-gray-900">{order.subtotal.toFixed(2)} DA</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">TVA</span>
                  <span className="text-gray-900">{order.vat_amount.toFixed(2)} DA</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Remise</span>
                    <span className="text-red-600">-{order.discount_amount.toFixed(2)} DA</span>
                  </div>
                )}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-emerald-600">
                      {order.total_amount.toFixed(2)} DA
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleEdit}
                  disabled={updateOrderStatus.isPending || deleteOrder.isPending}
                  className="flex-1 gap-2 border-2"
                >
                  <Edit className="w-4 h-4" />
                  Modifier
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleCancel}
                  disabled={updateOrderStatus.isPending || deleteOrder.isPending}
                  className="flex-1 gap-2 border-2 border-red-200 text-red-600 hover:bg-red-50"
                >
                  {deleteOrder.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                  Annuler
                </Button>
                <Button
                  size="lg"
                  onClick={handlePay}
                  disabled={updateOrderStatus.isPending || deleteOrder.isPending}
                  className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700"
                >
                  {updateOrderStatus.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  Payer
                </Button>
              </div>
              
              {/* Print Button */}
              <Button
                variant="outline"
                size="lg"
                onClick={() => setShowPrintManager(!showPrintManager)}
                className="w-full gap-2 border-2 border-blue-200 text-blue-600 hover:bg-blue-50"
              >
                <Printer className="w-4 h-4" />
                {showPrintManager ? 'Masquer' : 'Imprimer'}
              </Button>
            </div>

            {/* Print Manager */}
            {showPrintManager && (
              <div className="border-t border-gray-200 pt-4">
                <PrintManager order={order} />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
